import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { RM, RM0, RMk } from '../lib/format';
import { Skeletons } from './kit';
import * as earnApi from '../api/earnings';

/**
 * Total earning — one calendar year of the agent's own earnings, split by month
 * into commission (their own deals) and override (income their downline earned
 * them). The block Michael asked for: two headline figures over a twelve-month
 * chart, so a year reads at a glance instead of one period at a time.
 *
 * WHAT A MONTH MEANS HERE. The figures are what the agent EARNED on deals dated
 * in that month — not cash that reached them in it. That is not a shortcut: a
 * commission line has no date of its own beyond created_at, and nothing in the
 * schema records when a share was released or collected, so a "collected per
 * month" chart cannot be sourced at all (the Home hero's headline dropped its
 * month window for the same reason). The caption says so on screen, because
 * "Total earning" over a bar chart otherwise reads as money received.
 *
 * The two series always add up to the period tiles' EARNED for the same year —
 * same lines, same personal/override split, both computed server-side. Two
 * totals for one fact on one screen is the failure mode this block could most
 * easily introduce, so the endpoint reuses the period summary's own buckets.
 *
 * Colours are --series-1 / --series-2, two validated categorical slots in fixed
 * order (see design.css), never the status hues. Identity is never colour alone:
 * each figure carries its swatch AND its word, and the table view below repeats
 * every number the bars encode.
 *
 * Used by both builds — the phone Earnings page and the desktop one, which wraps
 * it in a Card. Everything here is plain CSS-variable markup, so it inherits
 * light/dark from the theme either way.
 */

const PLOT_H = { phone: 108, desktop: 148 };

// Nothing selected: point at the extreme, the one label worth carrying.
const peakOf = (months) => (months || []).reduce(
  (best, m) => (m.total > (best?.total ?? 0) ? m : best),
  null
);

/* Headline size for an exact figure. "RM 9,999,999" (12 characters) fits a
   card at the full size on a 320px phone; past that the size steps down in
   proportion to the length, so "RM 25,900,000.55" still sits on one line.
   Floored so it never turns into small print. */
const figureSize = (text, desktop) => {
  const base = desktop ? 18 : 14.5;
  const fits = desktop ? 13 : 12;
  const len = String(text).length;
  return len <= fits ? base : Math.max(desktop ? 13 : 11, +(base * fits / len).toFixed(1));
};

// Money columns keep a gutter on the left so nowrap figures never touch.
const MONEY_PAD = '4px 0 4px 10px';

const Swatch = ({ color }) => (
  <span style={{ width: 9, height: 9, borderRadius: 2, background: color, flex: 'none', display: 'inline-block' }} />
);

export default function EarningsMonthly({ desktop = false }) {
  const [year, setYear] = useState(null); // null = whatever the server defaults to (this year)
  // Bumped on every chip press so pressing the year that just failed retries it.
  // React batches it with setYear, so a press is still exactly one fetch.
  const [attempt, setAttempt] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  /* Two selections, not one. A tap on a phone fires an EMULATED mouseenter
     before its click, so a single `sel` that hover set and click toggled would
     turn itself straight back off and a tapped month would read as nothing
     happening. `hover` is a mouse's or the keyboard's, `pick` is the tap's or
     click's; what shows is hover when there is one, else the last pick. A
     finger never sets hover at all (see the bar handlers), so on a phone what
     shows is exactly what aria-pressed says is picked. */
  const [hover, setHover] = useState(null);
  const [pick, setPick] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const tableId = useId();
  /* The failed read, as { year } (the year ASKED for, null = the server's
     default), or null. Kept as the year rather than a flag because the message
     depends on it: a switch to 2023 that failed leaves 2025 on screen, but a
     background refresh of 2025 itself that failed leaves 2025 on screen too —
     and "that year could not be loaded" would then name the wrong year. */
  const [failed, setFailed] = useState(null);
  const sel = hover ?? pick;

  /* Only the newest read may land. Year chips can be pressed faster than the
     server answers, and responses do not come back in the order they were
     asked for: without this a slow 2023 arriving after 2024 would put 2023's
     bars under the 2024 chip, and the first read to finish would switch the
     loading dim off while the one that matters is still out. Every read —
     chip, retry, and the quiet cid:refresh one — takes a ticket, and a
     result, failure or loading change is applied only by the current ticket. */
  const seq = useRef(0);
  useEffect(() => () => { seq.current += 1; }, []); // unmounted: nothing lands

  /* A failed read must never come out as RM 0 — twelve empty months is a
     sentence about the agent's year, and this is the screen where that lie
     would be believed. It also happens to be the shape of the deploy-order
     trap: the PWA shipping before the agent server 404s this endpoint for
     everyone. So a failure says so and keeps whatever was already loaded. */
  const load = useCallback((y, { quiet = false } = {}) => {
    const ticket = ++seq.current;
    const current = () => ticket === seq.current;
    if (!quiet) setLoading(true);
    return earnApi.getEarningsMonthly(y ? { year: y } : {})
      .then((r) => { if (current()) { setData(r?.data ?? r); setFailed(null); } })
      .catch(() => { if (current()) setFailed({ year: y ?? null }); })
      .finally(() => { if (current()) setLoading(false); });
  }, []);

  useEffect(() => { load(year); }, [load, year, attempt]);

  // Money moving behind the agent's back — the same refresh the tiles listen for.
  // Re-read quietly: the chart holds its current bars rather than collapsing to a
  // skeleton for a change that usually moves nothing on this scale.
  useEffect(() => {
    const onRefresh = () => load(year, { quiet: true });
    window.addEventListener('cid:refresh', onRefresh);
    return () => window.removeEventListener('cid:refresh', onRefresh);
  }, [load, year]);

  const months = Array.isArray(data?.months) ? data.months : [];
  const totals = data?.totals || { commission: 0, override: 0, total: 0, deals: 0 };
  const shownYear = data?.year || year || new Date().getFullYear();
  const years = Array.isArray(data?.years) && data.years.length ? data.years : [shownYear];
  // The failed read was of the year already on screen (a cid:refresh, or a
  // retry of it) rather than a switch away from it. A null ask is the server's
  // default year, and with data on screen that can only be the one showing —
  // any chip press sets a real year.
  const refreshFailed = !!failed && (failed.year == null || failed.year === data?.year);

  // One scale for both series — they are the same measure in the same unit, so a
  // second axis would be a lie about their relative size.
  const max = useMemo(
    () => months.reduce((m, r) => Math.max(m, r.commission || 0, r.override || 0), 0),
    [months]
  );
  const peak = useMemo(() => peakOf(months), [months]);

  const plotH = PLOT_H[desktop ? 'desktop' : 'phone'];
  const barH = (v) => {
    const n = Number(v) || 0;
    if (n <= 0) return 0;
    if (max <= 0) return 0;
    return Math.max(3, Math.round((n / max) * plotH)); // a sliver, never nothing, for a real figure
  };

  const active = sel != null ? months[sel] : null;
  // A year with nothing in it gets the sentence, not an empty 12-column grid:
  // twelve labels under a bare baseline read as a chart that failed to load.
  const blank = !months.length || (totals.total === 0 && !months.some((m) => m.total));

  // First load owns a skeleton; every later one holds the current bars at
  // reduced opacity instead, so switching year never collapses the block.
  if (loading && !data) return <Skeletons n={1} height={desktop ? 260 : 210} />;
  if (failed && !data) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'baseline' }}>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Your monthly breakdown could not be loaded.</span>
        <button
          type="button"
          onClick={() => setAttempt((a) => a + 1)}
          style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--primary)', background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, opacity: loading && data ? 0.55 : 1 }}>
      {/* year selector — the block's own scope, independent of the page's period
          picker, because a monthly breakdown is only ever read one year at a time. */}
      <div className="scrx" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
        {years.map((y) => {
          const on = y === shownYear;
          return (
            <button
              key={y}
              type="button"
              /* the selection goes with the year: March of another year is a
                 different fact, so it is cleared here, on the event, not in an
                 effect that would run a render after the new months arrive */
              onClick={() => { setYear(y); setAttempt((a) => a + 1); setPick(null); setHover(null); }}
              aria-pressed={on}
              style={{
                flex: 'none', fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 999,
                cursor: 'pointer', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                background: on ? 'var(--primary)' : 'var(--surface2)',
                color: on ? '#fff' : 'var(--muted)',
                border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
              }}
            >
              {y}
            </button>
          );
        })}
      </div>

      {/* A year that failed to load leaves the last good one on screen, and the
          chips fall back to ITS year (shownYear reads data.year), so the figures
          and the highlighted chip can never disagree. */}
      {failed && (
        <div style={{ fontSize: 11.5, color: 'var(--warn)' }}>
          {refreshFailed
            ? `${shownYear} could not be refreshed — these figures may be out of date.`
            : `That year could not be loaded — still showing ${shownYear}.`}
        </div>
      )}

      {/* the two year figures. Swatch + word here IS the chart's legend, so the
          bars never have to be told apart by colour alone. */}
      {/* minmax(0,1fr), not 1fr: a bare 1fr column will not shrink below its
          content, so a long figure pushed the pair past a 320px screen. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
        {[
          { key: 'commission', label: 'COMMISSION', color: 'var(--series-1)', value: totals.commission },
          { key: 'override', label: 'OVERRIDE', color: 'var(--series-2)', value: totals.override },
        ].map((f) => {
          const text = RM0(f.value);
          return (
            <div key={f.key} style={{ minWidth: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '12px 12px 10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Swatch color={f.color} />
                <span style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600, letterSpacing: '.05em' }}>{f.label}</span>
              </div>
              {/* The exact figure, always — this is the number the agent quotes
                  back to HQ, so it shrinks to fit rather than turning into RMk.
                  The no-break space keeps "RM" on the figure's line; wrapping
                  mid-number is only the last resort for a figure nothing fits. */}
              <div style={{
                fontSize: figureSize(text, desktop), fontWeight: 700, marginTop: 4, color: 'var(--text)',
                fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere', lineHeight: 1.25,
              }}>
                {text.replace(' ', ' ')}
              </div>
            </div>
          );
        })}
      </div>

      {/* readout — the selected month, or the year's best month when nothing is
          picked. Always rendered, and tall enough for the two lines a month's
          figures wrap onto on a phone, so picking a month never walks the chart
          down the screen under the finger that picked it. */}
      <div aria-live="polite" style={{ fontSize: 11.5, color: 'var(--muted)', minHeight: desktop ? 17 : 34, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline' }}>
        {active ? (
          <>
            <b style={{ color: 'var(--text)', fontWeight: 600 }}>{active.label} {shownYear}</b>
            <span>commission <span style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{RM0(active.commission)}</span></span>
            <span>override <span style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{RM0(active.override)}</span></span>
            <span>· {active.deals} deal{active.deals === 1 ? '' : 's'}</span>
          </>
        ) : peak && peak.total > 0 ? (
          <span>Best month {peak.label} · <span style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{RM0(peak.total)}</span> — tap a month for its figures</span>
        ) : (
          <span>Nothing earned in {shownYear}.</span>
        )}
      </div>

      {/* the chart. Bars grow from one baseline on one scale; the cap label says
          what the tallest bar is worth, which is the whole job of a y-axis here. */}
      {!blank && (
      <div>
        <div style={{ fontSize: 10, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums', minHeight: 14 }}>
          {max > 0 ? RMk(max) : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: plotH, marginTop: 4 }}>
          {months.map((m, i) => {
            const on = sel === i;
            return (
              <button
                key={m.month}
                type="button"
                /* Toggling a month off also drops the hover on it, so the
                    readout and aria-pressed can never disagree: without this a
                    mouse or keyboard deselect left the month showing. Hover
                    here comes only from a real pointer (pointerType mouse/pen)
                    or keyboard focus (:focus-visible) — a finger's tap also
                    fires pointerenter and focus, and a hover set by those
                    would pin the month on screen after a second tap cleared it. */
                onClick={() => {
                  if (pick === i) { setPick(null); setHover(null); } else setPick(i);
                }}
                onFocus={(e) => { if (e.currentTarget.matches(':focus-visible')) setHover(i); }}
                onBlur={() => setHover((cur) => (cur === i ? null : cur))}
                onPointerEnter={(e) => { if (e.pointerType !== 'touch') setHover(i); }}
                onPointerLeave={(e) => { if (e.pointerType !== 'touch') setHover((cur) => (cur === i ? null : cur)); }}
                aria-pressed={pick === i}
                aria-label={`${m.label} ${shownYear}: commission ${RM0(m.commission)}, override ${RM0(m.override)}`}
                style={{
                  flex: 1, minWidth: 0, height: '100%', padding: 0, border: 0, cursor: 'pointer',
                  background: on ? 'var(--surface2)' : 'transparent',
                  borderRadius: 6,
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 2,
                }}
              >
                {/* 2px of surface between the pair does the separating — no strokes */}
                <span style={{ width: '42%', maxWidth: 24, height: barH(m.commission), background: 'var(--series-1)', borderRadius: '4px 4px 0 0', transition: 'height .45s cubic-bezier(.32,.72,0,1)' }} />
                <span style={{ width: '42%', maxWidth: 24, height: barH(m.override), background: 'var(--series-2)', borderRadius: '4px 4px 0 0', transition: 'height .45s cubic-bezier(.32,.72,0,1)' }} />
              </button>
            );
          })}
        </div>
        {/* the baseline the bars stand on: one hairline, never a grid */}
        <div style={{ height: 1, background: 'var(--border)' }} />
        <div style={{ display: 'flex', gap: 2, marginTop: 5 }}>
          {months.map((m, i) => (
            <span
              key={m.month}
              style={{
                flex: 1, minWidth: 0, textAlign: 'center', fontSize: 9.5, letterSpacing: '-.02em',
                color: sel === i ? 'var(--text)' : 'var(--muted)', fontWeight: sel === i ? 600 : 400,
              }}
            >
              {m.label}
            </span>
          ))}
        </div>
      </div>
      )}

      {!blank && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        {/* Says what the bars measure. Without it "Total earning" over a chart
            reads as money received, which is a different — and wrong — number. */}
        <span style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4 }}>
          Earned on deals dated in each month. Released and paid are on the tiles above.
        </span>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          aria-expanded={showTable}
          aria-controls={tableId}
          style={{ flex: 'none', fontSize: 11.5, fontWeight: 600, color: 'var(--primary)', background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
        >
          {showTable ? 'Hide figures' : 'Figures'}
        </button>
      </div>
      )}

      {/* every value the bars encode, reachable without hovering anything.
          Two decimals on every cell (RM, not RM0) so the right-aligned column
          lines up digit for digit; each money cell stays on one line so "RM"
          never orphans; and on a narrow phone the table scrolls inside its own
          box instead of dragging the whole page sideways. */}
      {showTable && !blank && (
        <div id={tableId} style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5, fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr style={{ color: 'var(--muted)', textAlign: 'right' }}>
              <th style={{ textAlign: 'left', fontWeight: 600, padding: '4px 0' }}>{shownYear}</th>
              <th style={{ fontWeight: 600, padding: MONEY_PAD }}>Commission</th>
              <th style={{ fontWeight: 600, padding: MONEY_PAD }}>Override</th>
              <th style={{ fontWeight: 600, padding: MONEY_PAD }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month} style={{ textAlign: 'right', borderTop: '1px solid var(--border)' }}>
                <td style={{ textAlign: 'left', padding: '4px 0', color: 'var(--muted)' }}>{m.label}</td>
                <td style={{ padding: MONEY_PAD, whiteSpace: 'nowrap' }}>{m.commission ? RM(m.commission) : '—'}</td>
                <td style={{ padding: MONEY_PAD, whiteSpace: 'nowrap' }}>{m.override ? RM(m.override) : '—'}</td>
                <td style={{ padding: MONEY_PAD, whiteSpace: 'nowrap', fontWeight: 600 }}>{m.total ? RM(m.total) : '—'}</td>
              </tr>
            ))}
            <tr style={{ textAlign: 'right', borderTop: '1px solid var(--border)', fontWeight: 700 }}>
              <td style={{ textAlign: 'left', padding: '6px 0' }}>Year</td>
              <td style={{ padding: '6px 0 6px 10px', whiteSpace: 'nowrap' }}>{RM(totals.commission)}</td>
              <td style={{ padding: '6px 0 6px 10px', whiteSpace: 'nowrap' }}>{RM(totals.override)}</td>
              <td style={{ padding: '6px 0 6px 10px', whiteSpace: 'nowrap' }}>{RM(totals.total)}</td>
            </tr>
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
